import React, { createContext, useContext, useState, useEffect } from 'react';
import { tenantAPI } from '../services/api';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children, user }) => {
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin';
  const canSwitchTenant = isSuperAdmin;

  const loadTenants = async () => {
    try {
      const response = await tenantAPI.getAll();
      const tenantList = response.data || [];
      setTenants(tenantList);

      if (user) {
        if (!canSwitchTenant) {
          const myTenant = tenantList.find(t => t.tenantId === user.tenantId);
          setCurrentTenant(myTenant || { tenantId: user.tenantId });
          localStorage.setItem('selectedTenantId', user.tenantId);
        } else {
          const savedTenantId = localStorage.getItem('selectedTenantId');
          const savedTenant = tenantList.find(t => t.tenantId === savedTenantId && t.isActive);
          if (savedTenant) {
            setCurrentTenant(savedTenant);
          } else if (tenantList.length > 0) {
            const first = tenantList.find(t => t.isActive) || tenantList[0];
            setCurrentTenant(first);
            localStorage.setItem('selectedTenantId', first.tenantId);
          }
        }
      } else if (tenantList.length > 0) {
        const first = tenantList.find(t => t.isActive) || tenantList[0];
        setCurrentTenant(first);
        localStorage.setItem('selectedTenantId', first.tenantId);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
      if (user && !canSwitchTenant) {
        setCurrentTenant({ tenantId: user.tenantId });
        localStorage.setItem('selectedTenantId', user.tenantId);
      }
    } finally {
      setLoading(false);
    }
  };

  const changeTenant = (tenant) => {
    if (!canSwitchTenant) return;
    setCurrentTenant(tenant);
    if (tenant) {
      localStorage.setItem('selectedTenantId', tenant.tenantId);
    } else {
      localStorage.removeItem('selectedTenantId');
    }
  };

  useEffect(() => {
    loadTenants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenantId, user?.role]);

  const value = {
    currentTenant,
    tenants,
    loading,
    changeTenant,
    reloadTenants: loadTenants,
    canSwitchTenant,
    isSuperAdmin
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
